import type { FragmentData } from "@gqlb/core";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { b } from "@/b";

export const MEDIA_FRAGMENT = b.fragment("MediaFragment", "Image", (b) => [
  b.id(),
  b.preview(),
  b.url(),
]);

function sizedImageUrl(image: FragmentData<typeof MEDIA_FRAGMENT>, size: number): string {
  if (image.url.startsWith("https://cdn.sanity.io/")) {
    const url = new URL(image.url);
    url.searchParams.set("w", String(size));
    url.searchParams.set("fit", "max");
    url.searchParams.set("auto", "format");
    url.searchParams.set("q", "75");
    return url.toString();
  }
  if (image.url.startsWith("https://cdn")) {
    return image.url;
  }
  return `/image/${image.id}?size=${size}&q=75`;
}

export const ResponsiveImage: React.FC<{
  image: FragmentData<typeof MEDIA_FRAGMENT>;
  objectFit: "cover" | "contain";
  forceSize?: number;
  lazy?: boolean;
  className?: string;
  style?: React.CSSProperties;
}> = ({ image, objectFit, forceSize, lazy = true, className, style }) => {
  const [activeSrc, setActiveSrc] = useState<string>(() => sizedImageUrl(image, 200));
  const [maxSize, setMaxSize] = useState<number | undefined>(undefined);

  useEffect(() => {
    async function loadImage() {
      if (maxSize === undefined) {
        return;
      }
      const rounding = maxSize > 400 ? 100 : 50;
      const size = Math.max(
        Math.min(forceSize ?? Math.round(maxSize / rounding) * rounding, 4800),
        200,
      );

      try {
        const url = sizedImageUrl(image, size);
        const img = new Image();
        img.src = url;
        await img.decode();
        setActiveSrc(url);
      } catch {}
    }
    loadImage();
  }, [forceSize, image, maxSize]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    setMaxSize(containerRef.current.clientWidth * window.devicePixelRatio);
    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      const viewedImageSize = width * window.devicePixelRatio;
      setMaxSize(viewedImageSize);
    });
    resizeObserver.observe(containerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className={`overflow-hidden flex ${className ?? ""}`} style={style}>
      {/* Blurred preview */}
      {/* biome-ignore lint/performance/noImgElement: intentional raw img for blur preview placeholder */}
      <img
        alt=""
        src={image.preview}
        className="block absolute inset-0 w-full h-full"
        style={{ objectFit, objectPosition: "center", filter: "blur(8px)" }}
      />
      {/* Full image */}
      {/* biome-ignore lint/performance/noImgElement: custom progressive loading with ResizeObserver */}
      <img
        alt=""
        src={activeSrc}
        loading={lazy ? "lazy" : "eager"}
        className="block absolute inset-0 w-full h-full"
        style={{ objectFit, objectPosition: "center" }}
      />
    </div>
  );
};
